<template>
  <!-- Teleported into #topbar-period-tabs (inside DesktopSubnav) -->
  <Teleport v-if="props.isActive && isTeleportReady" to="#topbar-period-tabs">
    <TopbarPeriodNav
      v-if="card && card.type !== 'custom'"
      :period-label="periodLabel"
      :period-offset="periodOffset"
      :period-transition="periodTransition"
      @shift-period="shiftPeriod($event)"
      @reset-period="resetPeriod"
    />
    <button
      v-if="activeSr"
      class="rds-edit-btn"
      @click="showEditSheet = true"
    >
      <Cog6ToothIcon class="size-[18px]" />
    </button>
  </Teleport>

  <div class="rds-shell">
    <DesktopSubnav
      :tabs="sectionTabs"
      :model-value="activeTab"
      @update:model-value="scrollToSection"
    />
    <ReportDesktopShellContent
      v-if="activeSr"
      :key="activeSr.id"
      ref="contentRef"
      :center-id="centerId"
      :sr-id="activeSr.id"
      @active-section="activeTab = $event"
      @navigate-tab="scrollToSection"
    />
  </div>

  <EditComponentSheet
    v-if="activeSr"
    v-model:isOpen="showEditSheet"
    :component-id="activeSr.id"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, nextTick, ref, watch, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSalesReportsStore } from '../../stores/salesReports'
import { usePeriodShift } from '../../composables/usePeriodShift'
import { useDesktopReportNav } from '../../composables/useDesktopReportNav'
import TopbarPeriodNav from './TopbarPeriodNav.vue'
import DesktopSubnav from './DesktopSubnav.vue'
import type { SubnavTab } from './DesktopSubnav.vue'
import ReportDesktopShellContent from './ReportDesktopShellContent.vue'
import type { SalesReport } from '../../models/SalesReport'
import {
  Squares2X2Icon,
  ChartBarIcon,
  ChartPieIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  IdentificationIcon,
  Cog6ToothIcon,
} from '@heroicons/vue/24/outline'
import EditComponentSheet from '../bottomsheet/EditComponentSheet.vue'

const props = defineProps<{
  centerId: number
  isActive: boolean
}>()

const { t } = useI18n()
const salesReportsStore = useSalesReportsStore()

// ── Active report selection (shared with sidebar) ──
const { activeSrIndex } = useDesktopReportNav()
const activeTab = ref('summary')
const showEditSheet = ref(false)

// Reset tab to summary when switching reports
watch(activeSrIndex, () => {
  activeTab.value = 'summary'
})

const salesReports = computed(() => salesReportsStore.salesReports ?? [])

const activeSr = computed(() => salesReports.value[activeSrIndex.value])

const card = computed(() => {
  if (!activeSr.value) return undefined
  return salesReportsStore.getReportForCenterAndComponent(props.centerId, activeSr.value.id)
})

const contentRef = ref<InstanceType<typeof ReportDesktopShellContent> | null>(null)

// ── Section tab definitions ──
const sectionTabs = computed<SubnavTab[]>(() => [
  { key: 'summary', label: t('navigation.summary'), icon: Squares2X2Icon },
  { key: 'sales', label: t('report.card_section.sales'), icon: ChartBarIcon },
  { key: 'families', label: t('report.card_section.families'), icon: ChartPieIcon },
  { key: 'clientTypes', label: t('report.card_section.client_types'), icon: BuildingOfficeIcon },
  { key: 'clients', label: t('report.card_section.clients'), icon: UserGroupIcon },
  { key: 'members', label: t('report.card_section.members'), icon: IdentificationIcon },
])

// ── Period shift (reads from the model via card computed) ──
const { periodOffset, periodTransition, periodLabel, shiftPeriod, resetPeriod } =
  usePeriodShift(card as Ref<SalesReport | undefined>, 'trt')

// ── Teleport readiness (targets may not exist on first render) ──
const isTeleportReady = ref(false)

onMounted(async () => {
  await nextTick()
  isTeleportReady.value = !!document.getElementById('topbar-period-tabs')
})

// ── Tab click → smooth scroll to section ──
const scrollToSection = (key: string) => {
  const el = document.getElementById(`rds-section-${key}`)
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
</script>

<!-- Scoped styles for the shell -->
<style scoped>
.rds-shell {
  position: relative;
  padding-top: 44px;
}

.rds-edit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: var(--muted-foreground);
  cursor: pointer;
  transition: all var(--duration-fast) ease;
  margin-left: 8px;
  flex-shrink: 0;
}

.rds-edit-btn:hover {
  background: var(--surface-hover);
  color: var(--foreground);
}
</style>

